<template>
    <div class="container admin-table-page">
        <!-- HEADER -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- FILTROS PRINCIPALES -->
        <div class="param-title-wrap niif-header" v-if="showFilters">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <b-form-select
                        v-model="selReporte"
                        @change="f_actualiza_reporte()"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectReport }}
                        </b-form-select-option>

                        <option
                            v-for="reporte in reportes"
                            :key="reporte.codigo"
                            :value="reporte.codigo"
                        >
                            {{ reporte?.codigo }} - {{ reporte?.nombre }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="selVigencia"
                        @change="f_actualiza_vigencia()"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>

                        <option
                            v-for="vigencia in listVigencia"
                            :key="vigencia.vigencia"
                            :value="vigencia.vigencia"
                        >
                            {{ vigencia?.vigencia }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="grupoId"
                        @change="filtraCompaniasPorGrupo"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="">
                            {{ labels.allGroups }}
                        </b-form-select-option>

                        <option
                            v-for="grupo in listGrupos"
                            :key="grupo.id"
                            :value="grupo.id"
                        >
                            {{ grupo?.nombre }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="companyId"
                        @change="f_actualiza_compania()"
                        class="filter-select ml-2"
                        :disabled="filteredCompanies.length === 0"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectCompany }}
                        </b-form-select-option>

                        <option
                            v-for="company in filteredCompanies"
                            :key="company.id"
                            :value="String(company.id)"
                        >
                            {{ company?.acronym ? company?.acronym + ' ' : '' }}{{ company?.nombre }}
                        </option>
                    </b-form-select>

                    <b-button
                        class="consult-btn ml-2"
                        @click="consultaECP"
                        :disabled="!isFormValid || isLoading"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>

                    <b-button
                        variant="link"
                        class="toggle-filters-btn ml-2"
                        @click="toggleFilters"
                        v-if="showTablas"
                    >
                        <font-awesome-icon :icon="['fas', 'chevron-up']" class="mr-1" />
                        {{ labels.hideFilters }}
                    </b-button>
                </div>
            </div>
        </div>

        <!-- FILTROS DE FILA Y COLUMNA -->
        <div
            class="param-title-wrap niif-header"
            v-if="showTablas && !showFilters"
        >
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center flex-wrap">

                    <b-button
                        variant="link"
                        class="toggle-filters-btn mr-2"
                        @click="toggleFilters"
                    >
                        <font-awesome-icon
                            :icon="['fas', 'chevron-down']"
                            class="mr-1"
                        />
                        {{ labels.showFilters }}
                    </b-button>

                    <!-- FILTRO FILA -->
                    <b-form-select
                        v-model="filtroFila"
                        @change="onFilaChange"
                        class="filter-select nivel-filter"
                        :disabled="listFilaFiltradas.length === 0"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectRow }}
                        </b-form-select-option>

                        <option
                            v-for="fila in listFilaFiltradas"
                            :key="fila.id"
                            :value="fila.id"
                        >
                            {{ fila.fila }} - {{ fila.descripcion }}
                        </option>
                    </b-form-select>

                    <!-- FILTRO COLUMNA -->
                    <b-form-select
                        v-model="filtroColumna"
                        @change="onColumnaChange"
                        class="filter-select nivel-filter ml-2"
                        :disabled="!filtroFila || isLoadingColumnas"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectColumn }}
                        </b-form-select-option>

                        <option
                            v-for="columna in listColumnasFiltradas"
                            :key="columna.id"
                            :value="columna.id"
                        >
                            {{ columna.columna }} - {{ columna.descripcion }}
                        </option>
                    </b-form-select>
                </div>
            </div>
        </div>

        <!-- TABLA PRINCIPAL - UNA SOLA TARJETA -->
        <div class="admin-table-block mt-4">

            <!-- ESTADO INICIAL - TARJETA BLANCA CON MENSAJE -->
            <div v-if="!showTablas && !isLoading">
                <CorporateDataTable
                    :items="[]"
                    :fields="[]"
                    :labels="tableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                size="3x"
                                class="text-muted mb-3"
                                style="opacity: 0.3;"
                            />
                            <p class="h4 text-muted">
                                {{ labels.selectFilters }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- LOADING -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">
                    {{ labels.loading }}
                </p>
            </div>

            <!-- TABLA DE FILAS -->
            <div v-if="mostrarTablaFilas && showTablas && filaTableData.length > 0" class="table-wrapper">
                <CorporateDataTable
                    :items="filaTableData"
                    :fields="filaFields"
                    :labels="filaTableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="filaFilterDefs"
                    :toolbar-disabled="false"
                    :show-pagination="true"
                    :show-rows-per-page="true"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="openCreateFila"
                                :disabled="isVigenciaBloqueada || !consultaRealizada"
                            >
                                {{ labels.createRow }}
                            </b-button>

                            <b-button
                                size="sm"
                                class="reset-btn-danger ml-1"
                                style="height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px;"
                                @click="borrarTodasFilas"
                                :disabled="filaTableData.length === 0 || isVigenciaBloqueada || !consultaRealizada"
                            >
                                <font-awesome-icon
                                    :icon="['fas', 'trash']"
                                    class="mr-1"
                                />
                                {{ labels.deleteAll }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(accionEdit)="slotProps">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            class="md-fab md-primary"
                            @click.stop="editFila(slotProps.item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon
                                :icon="['fas', 'pencil-alt']"
                                size="xs"
                            />
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="{ item }">
                        <b-button
                            class="reset-btn-danger"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            @click.stop="deleteFila(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon
                                :icon="['fas', 'trash']"
                                size="xs"
                            />
                        </b-button>
                    </template>

                    <template #empty>
                        <div class="text-center py-4">
                            <font-awesome-icon
                                :icon="['fas', 'table']"
                                class="empty-table-icon"
                            />
                            <p class="text-muted mt-2">
                                {{ labels.noRows }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- TABLA DE FILAS - VACÍA CON MENSAJE -->
            <div v-if="mostrarTablaFilas && showTablas && filaTableData.length === 0" class="table-wrapper">
                <CorporateDataTable
                    :items="[]"
                    :fields="filaFields"
                    :labels="filaTableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-4">
                            <font-awesome-icon
                                :icon="['fas', 'table']"
                                class="empty-table-icon"
                            />
                            <p class="text-muted mt-2">
                                {{ labels.noRows }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- TABLA DE COLUMNAS -->
            <div v-if="mostrarTablaColumnas && showTablas && columnaTableData.length > 0" class="table-wrapper">
                <CorporateDataTable
                    :items="columnaTableData"
                    :fields="columnaFields"
                    :labels="columnaTableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="columnaFilterDefs"
                    :toolbar-disabled="false"
                    :show-pagination="true"
                    :show-rows-per-page="true"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="openCreateColumna"
                                :disabled="!filtroFila || isVigenciaBloqueada || !consultaRealizada"
                            >
                                {{ labels.createColumn }}
                            </b-button>

                            <b-button
                                size="sm"
                                class="reset-btn-danger ml-1"
                                style="height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px;"
                                @click="borrarTodasColumnas"
                                :disabled="columnaTableData.length === 0 || isVigenciaBloqueada || !consultaRealizada"
                            >
                                <font-awesome-icon
                                    :icon="['fas', 'trash']"
                                    class="mr-1"
                                />
                                {{ labels.deleteAll }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(accionEdit)="slotProps">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            class="md-fab md-primary"
                            @click.stop="editColumna(slotProps.item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon
                                :icon="['fas', 'pencil-alt']"
                                size="xs"
                            />
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="{ item }">
                        <b-button
                            class="reset-btn-danger"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            @click.stop="deleteColumna(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon
                                :icon="['fas', 'trash']"
                                size="xs"
                            />
                        </b-button>
                    </template>

                    <template #empty>
                        <div class="text-center py-4">
                            <font-awesome-icon
                                :icon="['fas', 'table']"
                                class="empty-table-icon"
                            />
                            <p class="text-muted mt-2">
                                {{ labels.noColumns }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- TABLA DE COLUMNAS - VACÍA CON MENSAJE -->
            <div v-if="mostrarTablaColumnas && showTablas && columnaTableData.length === 0" class="table-wrapper">
                <CorporateDataTable
                    :items="[]"
                    :fields="columnaFields"
                    :labels="columnaTableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-4">
                            <font-awesome-icon
                                :icon="['fas', 'table']"
                                class="empty-table-icon"
                            />
                            <p class="text-muted mt-2">
                                {{ labels.noColumns }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- DETALLE ECP / FORMULACIONES -->
            <div v-if="mostrarDetalle && showTablas && detalleTableData.length > 0" class="table-wrapper">
                <CorporateDataTable
                    :items="detalleTableData"
                    :fields="detalleFields"
                    :labels="detalleTableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="detalleFilterDefs"
                    :toolbar-disabled="false"
                    :show-pagination="true"
                    :show-rows-per-page="true"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="newEcp"
                                :disabled="isVigenciaBloqueada"
                            >
                                Crear
                            </b-button>
                        </div>
                    </template>

                    <!-- Columna Contable -->
                    <template #cell(contable)="data">
                        <div class="text-center">
                            {{ data.item.contable === 1 ? 'Si' : 'No' }}
                        </div>
                    </template>

                    <!-- Columna Operación -->
                    <template #cell(operacion)="data">
                        <div class="text-left">
                            {{ data.item.operacion === '+' ? 'Suma' : data.item.operacion === '-' ? 'Resta' : data.item.operacion }}
                        </div>
                    </template>

                    <!-- Botón Editar -->
                    <template #cell(accionEdit)="slotProps">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            class="md-fab md-primary"
                            @click.stop="editCuenta(slotProps.item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon
                                :icon="['fas', 'pencil-alt']"
                                size="xs"
                            />
                        </b-button>
                    </template>

                    <!-- Botón Eliminar -->
                    <template #cell(accionDelete)="{ item }">
                        <b-button
                            class="reset-btn-danger"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            @click.stop="deleteDetaEcp(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon
                                :icon="['fas', 'trash']"
                                size="xs"
                            />
                        </b-button>
                    </template>

                    <template #empty>
                        <div class="text-center py-4">
                            <font-awesome-icon
                                :icon="['fas', 'table']"
                                class="empty-table-icon"
                            />
                            <p class="text-muted mt-2">
                                {{ labels.noDetail }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- DETALLE ECP - VACÍO CON MENSAJE -->
            <div v-if="mostrarDetalle && showTablas && detalleTableData.length === 0" class="table-wrapper">
                <CorporateDataTable
                    :items="[]"
                    :fields="detalleFields"
                    :labels="detalleTableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-4">
                            <font-awesome-icon
                                :icon="['fas', 'table']"
                                class="empty-table-icon"
                            />
                            <p class="text-muted mt-2">
                                {{ labels.noDetail }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>
        </div>

        <!-- MODALES -->
        <NewFilaVue
            :showFila="showModFila"
            :codReporte="selReporte"
            @closeModal="cierraModalFila"
            @reloadTblFila="recargaTblFila"
        />

        <NewColumna
            :showColumna="showModColumna"
            :codReporte="selReporte"
            :filaSelect="filtroFila"
            @closeModal="cierraModalColumna"
            @reloadTblColumna="recargaTblColumna"
        />

        <NewCuentaEcp 
            :modalCreaEcp="modalCreaEcp" 
            @cierraMod="closeMod" 
            :vigComSelect="infoVigeCom" 
            :cuentasCreadas="listCuentExist" 
            :readOnly="isVigenciaBloqueada" 
            @reloadTable="recargaTblDetalle"
        />
        
        <EditaCuentaEcp 
            :modalEditaEcp="modalEditaEcp" 
            :dataDetallEcp="infoDetEcp" 
            :readOnly="isVigenciaBloqueada" 
            @cierraMod="closeMod" 
            @reloadTable="recargaTblDetalle" 
        />
        
        <EditMasivoEcp 
            :modalEditAllEcp="modalEditAllEcp" 
            :dataVigComFilCol="consulDetallEcp" 
            :readOnly="isVigenciaBloqueada" 
            @closeModal="closeMod" 
            @reloadTable="recargaTblDetalle"
        />

        <!-- MODAL CONFIRMACIÓN ELIMINAR -->
        <b-modal
            :model-value="showConfirmDeleteModal"
            :no-close-on-backdrop="true"
            hide-footer
                        centered
            size="md"
            :title="labels.confirmDelete"
            header-class="reset-modal-header"
            title-class="reset-modal-title"
            body-class="reset-modal-body"
         @hide="closeConfirmDeleteModal">
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">
                        {{ labels.areYouSure }}
                    </div>

                    <div class="password-policy-subtitle">
                        {{ labels.confirmDeleteText }}
                    </div>
                </div>

                <div class="reset-dialog-actions d-flex justify-content-end mt-4">
                    <b-button
                        class="reset-btn-secondary"
                        @click="closeConfirmDeleteModal"
                    >
                        {{ labels.cancel }}
                    </b-button>

                    <b-button
                        class="reset-btn-primary ml-2"
                        @click="executeDelete"
                        style="background-color: #cd2838; border-color: #cd2838;"
                    >
                        {{ labels.confirm }}
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import Balance from '../../services/BalanceNiif'
import PlanCuentas from '../../services/PlanCuentas'
import Company from '../../services/Company'
import EcpReporte from '../../services/EcpReporte'
import Grupo from '../../services/Grupo'
import CorporateDataTable from '../utils/CorporateDataTable.vue'
import NewFilaVue from './NewFila.vue'
import NewColumna from './NewColumna.vue'
import NewCuentaEcp from './NewCuentaEcp.vue'
import EditaCuentaEcp from './EditaCuentaEcp.vue'
import EditMasivoEcp from './EditMasivoEcp.vue'

export default {
    name: 'EcpReporte',

    components: {
        CorporateDataTable,
        NewFilaVue,
        NewColumna,
        NewCuentaEcp,
        EditaCuentaEcp,
        EditMasivoEcp
    },

    data: () => ({
        selReporte: '',
        selVigencia: '',
        companyId: '',
        grupoId: '',

        listVigencia: [],
        listCompany: [],
        listGrupos: [],
        filteredCompanies: [],
        reportes: [],
        companiaUsuario: [],

        isLoading: false,
        isLoadingColumnas: false,

        showFilters: true,
        showTablas: false,
        consultaRealizada: false,

        vigencia_anterior: '',
        compania_anterior: '',
        codReporteAnterior: '',

        listFila: [],
        listColumna: [],

        filtroFila: '',
        filtroColumna: '',

        detalleEcpData: [],
        listCuentExist: [],

        showModFila: false,
        showModColumna: false,

        showConfirmDeleteModal: false,
        deleteId: null,
        isDeleteAll: false,
        deleteTarget: 'fila',

        infoVigeCom: {
            vigencia: "",
            companyId: "",
            compania: "",
            codReporte: ""
        },

        // Modales de detalle
        modalCreaEcp: false,
        modalEditaEcp: false,
        modalEditAllEcp: false,
        infoDetEcp: {},
        consulDetallEcp: {}
    }),

    computed: {
        labels() {
            if (this.$store.state._language == "es-ES") {
                return {
                    title: "Reporte ECP",
                    selectReport: "Seleccione Reporte",
                    selectValidity: "Seleccione Vigencia",
                    selectCompany: "Seleccione Compañía",
                    allGroups: "Todos los grupos",
                    consultar: "Consultar",
                    showFilters: "Mostrar filtros",
                    hideFilters: "Ocultar filtros",
                    selectRow: "Fila",
                    selectColumn: "Columna",
                    selectFilters: "Seleccione los filtros y consulte",
                    loading: "Cargando...",
                    createRow: "Crear Fila",
                    createColumn: "Crear Columna",
                    deleteAll: "Eliminar Todo",
                    edit: "Editar",
                    delete: "Eliminar",
                    codigo: "Código",
                    descripcion: "Descripción",
                    noRows: "No hay filas disponibles",
                    noColumns: "No hay columnas disponibles",
                    confirmDelete: "Confirmar Eliminación",
                    areYouSure: "¿Está seguro?",
                    confirmDeleteText: "¿Está seguro que desea eliminar este registro?",
                    cancel: "Cancelar",
                    confirm: "Confirmar",
                    noMatches: "No se encontraron resultados",
                    searchPlaceholder: "Buscar en la tabla...",
                    rowsPerPage: "Filas por página:",
                    noSort: "Sin orden",
                    sortAsc: "A-Z / Menor a mayor",
                    sortDesc: "Z-A / Mayor a menor",
                    columns: "Columnas",
                    showAllColumns: "Mostrar todas",
                    filters: "Filtros",
                    clearFilters: "Limpiar filtros",
                    detail: "Detalle",
                    contable: "Contable",
                    cuenta: "Cuenta",
                    actual: "Actual",
                    operacion: "Operación",
                    formulacion: "Formulación",
                    noDetail: "No hay detalles disponibles"
                }
            } else {
                return {
                    title: "ECP Report",
                    selectReport: "Select Report",
                    selectValidity: "Select Validity",
                    selectCompany: "Select Company",
                    allGroups: "All groups",
                    consultar: "Consult",
                    showFilters: "Show filters",
                    hideFilters: "Hide filters",
                    selectRow: "Row",
                    selectColumn: "Column",
                    selectFilters: "Select filters and consult",
                    loading: "Loading...",
                    createRow: "Create Row",
                    createColumn: "Create Column",
                    deleteAll: "Delete All",
                    edit: "Edit",
                    delete: "Delete",
                    codigo: "Code",
                    descripcion: "Description",
                    noRows: "No rows available",
                    noColumns: "No columns available",
                    confirmDelete: "Confirm Delete",
                    areYouSure: "Are you sure?",
                    confirmDeleteText: "Are you sure you want to delete this record?",
                    cancel: "Cancel",
                    confirm: "Confirm",
                    noMatches: "No results found",
                    searchPlaceholder: "Search in table...",
                    rowsPerPage: "Rows per page:",
                    noSort: "No sorting",
                    sortAsc: "A-Z / Low to high",
                    sortDesc: "Z-A / High to low",
                    columns: "Columns",
                    showAllColumns: "Show all",
                    filters: "Filters",
                    clearFilters: "Clear filters",
                    detail: "Detail",
                    contable: "Accountant",
                    cuenta: "Account",
                    actual: "Actual",
                    operacion: "Operation",
                    formulacion: "Formulation",
                    noDetail: "No details available"
                }
            }
        },

        isFormValid() {
            return this.selReporte && this.selVigencia && this.companyId
        },

        isVigenciaBloqueada() {
            return this.selVigencia
                ? !this.$store.getters.isPeriodoEnabled(this.selVigencia)
                : false
        },

        mostrarTablaFilas() {
            return this.filtroFila === ''
        },

        mostrarTablaColumnas() {
            return this.filtroFila !== '' && this.filtroColumna === ''
        },

        mostrarDetalle() {
            return this.filtroFila !== '' && this.filtroColumna !== ''
        },

        listFilaFiltradas() {
            const filas = Array.isArray(this.listFila) ? this.listFila : []

            return filas.filter(fila => {
                const codigoReporte =
                    fila?.codReporte ??
                    fila?.cod_reporte ??
                    fila?.codigoReporte ??
                    fila?.reporte

                return (
                    codigoReporte === undefined ||
                    codigoReporte === null ||
                    String(codigoReporte) === String(this.selReporte)
                )
            })
        },

        listColumnasFiltradas() {
            const columnas = Array.isArray(this.listColumna) ? this.listColumna : []

            return columnas.filter(columna => {
                const codigoReporte =
                    columna?.codReporte ??
                    columna?.cod_reporte ??
                    columna?.codigoReporte ??
                    columna?.reporte

                const coincideReporte =
                    codigoReporte === undefined ||
                    codigoReporte === null ||
                    String(codigoReporte) === String(this.selReporte)

                return coincideReporte
            })
        },

        filaTableData() {
            return this.listFilaFiltradas.map(item => ({
                id: item.id,
                codigo: item.fila,
                descripcion: item.descripcion
            }))
        },

        columnaTableData() {
            return this.listColumnasFiltradas.map(item => ({
                id: item.id,
                codigo: item.columna,
                descripcion: item.descripcion
            }))
        },

        detalleTableData() {
            if (!this.detalleEcpData || !Array.isArray(this.detalleEcpData)) {
                return []
            }

            return this.detalleEcpData.map(item => ({
                id: item.id,
                contable: item.contable,
                cuenta: item.cuenta || '-',
                actual: item.actual,
                operacion: item.operacion || '-',
                formulacion: this.getFormulacion(item.formulacion, item.contable)
            }))
        },

        filaFields() {
            return [
                {
                    key: 'codigo',
                    label: this.labels.codigo,
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle'
                },
                {
                    key: 'descripcion',
                    label: this.labels.descripcion,
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left align-middle'
                },
                {
                    key: 'accionEdit',
                    label: this.labels.edit,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle',
                    sortable: false
                },
                {
                    key: 'accionDelete',
                    label: this.labels.delete,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle',
                    sortable: false
                }
            ]
        },

        columnaFields() {
            return [
                {
                    key: 'codigo',
                    label: this.labels.codigo,
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle'
                },
                {
                    key: 'descripcion',
                    label: this.labels.descripcion,
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left align-middle'
                },
                {
                    key: 'accionEdit',
                    label: this.labels.edit,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle',
                    sortable: false
                },
                {
                    key: 'accionDelete',
                    label: this.labels.delete,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle',
                    sortable: false
                }
            ]
        },

        // Campos del detalle CON acciones (editar y eliminar)
        detalleFields() {
            return [
                {
                    key: 'contable',
                    label: this.labels.contable,
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle'
                },
                {
                    key: 'cuenta',
                    label: this.labels.cuenta,
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left align-middle'
                },
                {
                    key: 'actual',
                    label: this.labels.actual,
                    sortable: true,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle'
                },
                {
                    key: 'operacion',
                    label: this.labels.operacion,
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left align-middle'
                },
                {
                    key: 'formulacion',
                    label: this.labels.formulacion,
                    sortable: true,
                    thClass: 'text-left',
                    tdClass: 'text-left align-middle'
                },
                {
                    key: 'accionEdit',
                    label: this.labels.edit,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle',
                    sortable: false
                },
                {
                    key: 'accionDelete',
                    label: this.labels.delete,
                    thClass: 'text-center',
                    tdClass: 'text-center align-middle',
                    sortable: false
                }
            ]
        },

        filaFilterDefs() {
            return [
                {
                    key: 'codigo',
                    type: 'text',
                    placeholder: 'Filtrar código...'
                },
                {
                    key: 'descripcion',
                    type: 'text',
                    placeholder: 'Filtrar descripción...'
                }
            ]
        },

        columnaFilterDefs() {
            return [
                {
                    key: 'codigo',
                    type: 'text',
                    placeholder: 'Filtrar código...'
                },
                {
                    key: 'descripcion',
                    type: 'text',
                    placeholder: 'Filtrar descripción...'
                }
            ]
        },

        detalleFilterDefs() {
            return [
                {
                    key: 'contable',
                    type: 'text',
                    placeholder: 'Filtrar contable...'
                },
                {
                    key: 'cuenta',
                    type: 'text',
                    placeholder: 'Filtrar cuenta...'
                },
                {
                    key: 'actual',
                    type: 'text',
                    placeholder: 'Filtrar actual...'
                },
                {
                    key: 'operacion',
                    type: 'text',
                    placeholder: 'Filtrar operación...'
                },
                {
                    key: 'formulacion',
                    type: 'text',
                    placeholder: 'Filtrar formulación...'
                }
            ]
        },

        tableLabels() {
            return {
                noRow: this.labels.noRows,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },

        columnaTableLabels() {
            return {
                noRow: this.labels.noColumns,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },

        detalleTableLabels() {
            return {
                noRow: this.labels.noDetail,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        }
    },

    watch: {
        selVigencia(valNew) {
            if (valNew !== this.vigencia_anterior) {
                this.vigencia_anterior = valNew

                if (this.isVigenciaBloqueada) {
                    this.$toast.error(
                        'La vigencia seleccionada está deshabilitada para crear o editar.'
                    )
                }
            }
        }
    },

    methods: {
        goBack() {
            this.$router.back()
        },

        toggleFilters() {
            this.showFilters = !this.showFilters
        },

        /**
         * Filtra las compañías según el grupo seleccionado
         */
        filtraCompaniasPorGrupo(valor = null) {
            const grupoIdSeleccionado = valor !== null ? valor : this.grupoId
            
            // Resetear los datos al cambiar el grupo
            this.onFiltroChange()
            this.companyId = ''

            // Si no hay grupo seleccionado o es "all", mostrar todas las compañías
            if (!grupoIdSeleccionado || grupoIdSeleccionado === '') {
                this.filteredCompanies = [...this.listCompany]
                return
            }

            const grupoIdStr = grupoIdSeleccionado.toString()
            
            // Filtrar compañías que pertenecen al grupo seleccionado
            this.filteredCompanies = this.listCompany.filter(company => {
                if (!company?.id_grupos) {
                    return false
                }

                const gruposCompania = company.id_grupos
                    .split(',')
                    .map(id => id.trim())
                    .filter(id => id !== '')

                return gruposCompania.includes(grupoIdStr)
            })
        },

        f_actualiza_compania() {
            if (this.companyId) {
                this.resetData()
                this.compania_anterior = this.companyId
            }
        },

        f_actualiza_reporte() {
            if (this.codReporteAnterior != this.selReporte) {
                this.resetData()
                this.codReporteAnterior = this.selReporte
            }
        },

        f_actualiza_vigencia() {
            if (this.vigencia_anterior != this.selVigencia) {
                this.resetData()
                this.vigencia_anterior = this.selVigencia
            }

            if (this.isVigenciaBloqueada) {
                this.$toast.error(
                    'La vigencia seleccionada está deshabilitada para crear o editar.'
                )
            }
        },

        /**
         * Resetear datos cuando cambia un filtro principal
         */
        onFiltroChange() {
            this.showTablas = false
            this.consultaRealizada = false

            this.showModFila = false
            this.showModColumna = false

            this.isLoadingColumnas = false

            this.filtroFila = ''
            this.filtroColumna = ''

            this.listFila = []
            this.listColumna = []

            this.detalleEcpData = []
            this.listCuentExist = []

            this.showFilters = true
        },

        resetData() {
            this.showTablas = false
            this.consultaRealizada = false

            this.showModFila = false
            this.showModColumna = false

            this.isLoadingColumnas = false

            this.filtroFila = ''
            this.filtroColumna = ''

            this.listFila = []
            this.listColumna = []

            this.detalleEcpData = []
            this.listCuentExist = []

            this.showFilters = true
        },

        consultaECP() {
            if (!this.isFormValid) {
                this.$toast.error(
                    "Diligenciar los campos: reporte, vigencia y compañía"
                )
                return
            }

            this.isLoading = true
            this.consultaRealizada = true

            this.infoVigeCom = {
                vigencia: this.selVigencia,
                companyId: this.companyId,
                compania: this.companyId,
                codReporte: this.selReporte
            }

            this.cargarFilasYColumnas()
        },

        cargarFilasYColumnas() {
            this.isLoading = true

            Promise.all([
                EcpReporte.getAllFila(),
                EcpReporte.getAllColumna()
            ])
                .then(([resFilas, resColumnas]) => {
                    const filas = resFilas?.data?.allFila || resFilas?.data?.filas || []
                    this.listFila = Array.isArray(filas) ? filas : []

                    const columnas = resColumnas?.data?.allColumn || resColumnas?.data?.columnas || []
                    this.listColumna = Array.isArray(columnas) ? columnas : []

                    this.filtroFila = ''
                    this.filtroColumna = ''

                    this.detalleEcpData = []
                    this.listCuentExist = []

                    this.showTablas = true
                    this.showFilters = false
                    this.consultaRealizada = true
                })
                .catch((err) => {
                    console.error(err)

                    this.listFila = []
                    this.listColumna = []
                    this.detalleEcpData = []
                    this.listCuentExist = []

                    this.$toast.error(
                        'Error al cargar los datos'
                    )
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        onFilaChange() {
            this.filtroColumna = ''
            this.detalleEcpData = []
            this.listCuentExist = []
        },

        cargarDetalleEcp() {
            // Validar que tengamos fila y columna seleccionadas
            if (!this.filtroFila || !this.filtroColumna) {
                this.detalleEcpData = []
                this.listCuentExist = []
                return
            }

            // Obtener el objeto de la fila seleccionada
            const filaSeleccionada = this.listFila.find(f => f.id === this.filtroFila)
            const columnaSeleccionada = this.listColumna.find(c => c.id === this.filtroColumna)

            // Validar que existan
            if (!filaSeleccionada) {
                this.$toast.error('Error al obtener el código de la fila')
                return
            }

            if (!columnaSeleccionada) {
                this.$toast.error('Error al obtener el código de la columna')
                return
            }

            // Construir el objeto de consulta con los códigos reales
            const infoSel = {
                vigencia: this.selVigencia,
                companyId: this.companyId,
                codReporte: this.selReporte,
                fila: filaSeleccionada.fila,
                columna: columnaSeleccionada.columna
            }

            // Guardar para edición masiva
            this.consulDetallEcp = infoSel

            // Mostrar loading
            this.isLoadingColumnas = true

            // Llamar al servicio
            EcpReporte.consDetalleEcp(infoSel)
                .then((res) => {
                    if (res.status === 200) {
                        const data = res?.data?.detEcp || []
                        this.detalleEcpData = data

                        // Construir lista de cuentas existentes
                        this.listCuentExist = []
                        data.forEach((elem) => {
                            if (elem.cuenta != null) {
                                this.listCuentExist.push({
                                    id_compania: elem.id_compania,
                                    vigencia: elem.vigencia,
                                    fila: elem.fila,
                                    columna: elem.columna,
                                    cuenta: elem.cuenta
                                })
                            }
                        })
                    } else if (res.status === 204) {
                        this.detalleEcpData = []
                        this.listCuentExist = []
                    }
                })
                .catch((err) => {
                    console.error(err)
                    if (err.response?.status === 404 || err.response?.status === 204) {
                        this.detalleEcpData = []
                        this.listCuentExist = []
                    } else {
                        this.detalleEcpData = []
                        this.listCuentExist = []
                        this.$toast.error('Error al cargar los detalles')
                    }
                })
                .finally(() => {
                    this.isLoadingColumnas = false
                })
        },

        onColumnaChange() {
            // Limpiar datos anteriores
            this.detalleEcpData = []
            this.listCuentExist = []

            // Si no hay fila seleccionada, no hacer nada
            if (!this.filtroFila) {
                return
            }

            // Usar $nextTick para asegurar que el v-model se actualizó
            this.$nextTick(() => {
                // Ahora this.filtroColumna ya tiene el valor seleccionado
                if (this.filtroColumna) {
                    this.cargarDetalleEcp()
                }
            })
        },

        // Métodos de detalle
        getFormulacion(valFormulacion, valContable) {
            if (valContable == 1) {
                return ""
            } else {
                return valFormulacion || '-'
            }
        },

        newEcp() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            this.modalCreaEcp = true
        },

        async editCuenta(item) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            try {
                let info = await EcpReporte.getDetCuentaEcpById(item.id)
                this.infoDetEcp = Array.isArray(info.data.detCuentEcp) 
                    ? info.data.detCuentEcp 
                    : [info.data.detCuentEcp]
                this.modalEditaEcp = true
            } catch (err) {
                console.error(err)
                this.$toast.error('Error al cargar los datos de la cuenta')
            }
        },

        editAllEcp() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            if (this.detalleEcpData.length === 0) {
                this.$toast.error('No hay registros para editar')
                return
            }
            this.modalEditAllEcp = true
        },

        deleteDetaEcp(item) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.deleteId = item.id
            this.deleteTarget = 'detalle'
            this.isDeleteAll = false
            this.showConfirmDeleteModal = true
        },

        deleteAllEcp() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            if (this.detalleEcpData.length === 0) {
                this.$toast.error('No hay registros para eliminar')
                return
            }
            this.deleteId = null
            this.deleteTarget = 'detalle'
            this.isDeleteAll = true
            this.showConfirmDeleteModal = true
        },

        recargaTblDetalle() {
            this.closeMod()
            if (this.filtroFila && this.filtroColumna) {
                this.cargarDetalleEcp()
            }
        },

        closeMod() {
            this.modalCreaEcp = false
            this.modalEditaEcp = false
            this.modalEditAllEcp = false
        },

        openCreateFila() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(
                    'La vigencia seleccionada está deshabilitada para crear o editar.'
                )
                return
            }

            this.showModFila = true
        },

        editFila(item) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(
                    'La vigencia seleccionada está deshabilitada para crear o editar.'
                )
                return
            }

            this.$toast.info(
                'Acción restringida: Acceso administrador ' +
                (item?.codigo || '')
            )
        },

        deleteFila(item) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(
                    'La vigencia seleccionada está deshabilitada para eliminar.'
                )
                return
            }

            this.deleteId = item.id
            this.deleteTarget = 'fila'
            this.isDeleteAll = false
            this.showConfirmDeleteModal = true
        },

        borrarTodasFilas() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(
                    'La vigencia seleccionada está deshabilitada para eliminar.'
                )
                return
            }

            this.deleteId = null
            this.deleteTarget = 'fila'
            this.isDeleteAll = true
            this.showConfirmDeleteModal = true
        },

        recargaTblFila(val) {
            this.showModFila = val
            this.cargarFilasYColumnas()
        },

        openCreateColumna() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(
                    'La vigencia seleccionada está deshabilitada para crear o editar.'
                )
                return
            }

            if (!this.filtroFila) {
                this.$toast.error(
                    'Primero debe seleccionar una fila.'
                )
                return
            }

            this.showModColumna = true
        },

        editColumna(item) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(
                    'La vigencia seleccionada está deshabilitada para crear o editar.'
                )
                return
            }

            this.$toast.info(
                'Acción restringida: Acceso administrador ' +
                (item?.codigo || '')
            )
        },

        deleteColumna(item) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(
                    'La vigencia seleccionada está deshabilitada para eliminar.'
                )
                return
            }

            this.deleteId = item.id
            this.deleteTarget = 'columna'
            this.isDeleteAll = false
            this.showConfirmDeleteModal = true
        },

        borrarTodasColumnas() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(
                    'La vigencia seleccionada está deshabilitada para eliminar.'
                )
                return
            }

            this.deleteId = null
            this.deleteTarget = 'columna'
            this.isDeleteAll = true
            this.showConfirmDeleteModal = true
        },

        recargaTblColumna(val) {
            this.showModColumna = val

            EcpReporte.getAllColumna()
                .then((res) => {
                    const columnas = res?.data?.allColumn || res?.data?.columnas || []
                    this.listColumna = Array.isArray(columnas) ? columnas : []
                })
                .catch((err) => {
                    console.error(err)
                    this.listColumna = []
                })

            this.filtroColumna = ''
            this.detalleEcpData = []
            this.listCuentExist = []
        },

        closeConfirmDeleteModal() {
            this.showConfirmDeleteModal = false
            this.deleteId = null
            this.isDeleteAll = false
            this.deleteTarget = 'fila'
        },

        executeDelete() {
            if (this.deleteTarget === 'fila') {

                if (this.isDeleteAll) {
                    EcpReporte.deleteAllFilas(this.selReporte)
                        .then(() => {
                            this.$toast.success(
                                'Todas las filas eliminadas.'
                            )

                            this.showConfirmDeleteModal = false

                            this.cargarFilasYColumnas()
                        })
                        .catch((err) => {
                            console.error(err)

                            this.$toast.error(
                                'Error al eliminar las filas'
                            )
                        })
                } else {
                    EcpReporte.deleteFila(this.deleteId)
                        .then(() => {
                            this.$toast.success(
                                'Fila eliminada.'
                            )

                            this.showConfirmDeleteModal = false

                            this.cargarFilasYColumnas()
                        })
                        .catch((err) => {
                            console.error(err)

                            this.$toast.error(
                                'Error al eliminar la fila'
                            )
                        })
                }

            } else if (this.deleteTarget === 'columna') {

                if (this.isDeleteAll) {
                    EcpReporte.deleteAllColumnas(this.selReporte, this.filtroFila)
                        .then(() => {
                            this.$toast.success(
                                'Todas las columnas eliminadas.'
                            )

                            this.showConfirmDeleteModal = false

                            EcpReporte.getAllColumna()
                                .then((res) => {
                                    const columnas = res?.data?.allColumn || res?.data?.columnas || []
                                    this.listColumna = Array.isArray(columnas) ? columnas : []
                                })
                                .catch((err) => {
                                    console.error(err)
                                    this.listColumna = []
                                })
                        })
                        .catch((err) => {
                            console.error(err)

                            this.$toast.error(
                                'Error al eliminar las columnas'
                            )
                        })
                } else {
                    EcpReporte.deleteColumna(this.deleteId)
                        .then(() => {
                            this.$toast.success(
                                'Columna eliminada.'
                            )

                            this.showConfirmDeleteModal = false

                            EcpReporte.getAllColumna()
                                .then((res) => {
                                    const columnas = res?.data?.allColumn || res?.data?.columnas || []
                                    this.listColumna = Array.isArray(columnas) ? columnas : []
                                })
                                .catch((err) => {
                                    console.error(err)
                                    this.listColumna = []
                                })
                        })
                        .catch((err) => {
                            console.error(err)

                            this.$toast.error(
                                'Error al eliminar la columna'
                            )
                        })
                }

            } else if (this.deleteTarget === 'detalle') {

                if (this.isDeleteAll) {
                    EcpReporte.deleteAllDetEcp(this.infoVigeCom)
                        .then((res) => {
                            if (res.status == 200) {
                                this.$toast.success(res.data.message || 'Registros eliminados correctamente')
                                this.showConfirmDeleteModal = false
                                this.recargaTblDetalle()
                            }
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar los registros')
                        })
                } else {
                    EcpReporte.deleteDetCuentaEcp(this.deleteId)
                        .then((res) => {
                            if (res.status == 200) {
                                this.$toast.success("Se ha eliminado el registro.")
                                this.showConfirmDeleteModal = false
                                this.recargaTblDetalle()
                            }
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar el registro')
                        })
                }
            }
        },

        cierraModalFila(val) {
            this.showModFila = val
        },

        cierraModalColumna(val) {
            this.showModColumna = val
        },

        cargarDatosIniciales() {
            this.isLoading = true

            Promise.all([
                Balance.leaCompaniaUsuario(),
                PlanCuentas.getVigenciaPlancuentas(),
                Company.getAllCompanies(),
                Balance.getReportes(),
                Grupo.getAllGrupos()
            ])
                .then(
                    ([
                        companiaRes,
                        vigenciaRes,
                        companyRes,
                        reportesRes,
                        grupoRes
                    ]) => {

                        this.companiaUsuario =
                            companiaRes.data.companiaUsuario || []

                        this.listVigencia =
                            vigenciaRes.data.getVigencia || []

                        this.reportes =
                            reportesRes.data.reportes || []

                        this.listGrupos =
                            grupoRes.data.grupos || []

                        let v_companias =
                            companyRes.data.companies || []

                        if (this.$store.state.userRole != 1) {
                            v_companias =
                                v_companias.filter(element => {
                                    return this.companiaUsuario.some(
                                        r_usuario =>
                                            r_usuario.id_usuario ==
                                                this.$store.state.userId &&
                                            r_usuario.id_compania ==
                                                element.id
                                    )
                                })
                        }

                        this.listCompany = v_companias
                        this.filteredCompanies = [...v_companias]
                    }
                )
                .catch((err) => {
                    console.error(err)

                    this.$toast.error(
                        'Error al cargar los datos iniciales'
                    )
                })
                .finally(() => {
                    this.isLoading = false
                })
        }
    },

    mounted() {
        this.cargarDatosIniciales()
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.niif-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #28a745;
    color: white;
    border: 1px solid #28a745;
}

.table-toolbar-add-btn:hover:not(:disabled) {
    background-color: #218838;
    border-color: #1e7e34;
}

.table-toolbar-add-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }

    .toggle-filters-btn {
        font-size: 13px;
        height: 36px;
        padding: 4px 10px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    
    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }

    .toggle-filters-btn {
        font-size: 12px;
        height: 36px;
        padding: 4px 8px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    
    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    
    .niif-header .filters-container {
        gap: 4px;
    }
    
    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }

    .toggle-filters-btn {
        width: 100%;
        justify-content: center;
        height: 36px;
        margin-bottom: 4px;
        margin-right: 0 !important;
    }
}

.toggle-filters-btn {
    color: #2e507a;
    font-weight: 500;
    font-size: 14px;
    padding: 4px 12px;
    text-decoration: none;
    border: 1px solid #dee2e6;
    border-radius: 7px;
    background: white;
    height: 38px;
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
}

.toggle-filters-btn:hover {
    background: #f8f9fa;
    text-decoration: none;
    color: #1a3455;
}
</style>